import type { FunctionalComponent, SVGAttributes } from 'vue'
import IMdiAccountGroup from '~icons/mdi/account-group'
import IMdiAccountOutline from '~icons/mdi/account-outline'
import IMdiArrowExpandAll from '~icons/mdi/arrow-expand-all'
import IMdiAspectRatio from '~icons/mdi/aspect-ratio'
import IMdiCalendar from '~icons/mdi/calendar'
import IMdiCalendarAlert from '~icons/mdi/calendar-alert'
import IMdiCalendarArrowRight from '~icons/mdi/calendar-arrow-right'
import IMdiCalendarClock from '~icons/mdi/calendar-clock'
import IMdiCalendarEnd from '~icons/mdi/calendar-end'
import IMdiCalendarImport from '~icons/mdi/calendar-import'
import IMdiCalendarMultiselect from '~icons/mdi/calendar-multiselect'
import IMdiCalendarPlus from '~icons/mdi/calendar-plus'
import IMdiCalendarQuestion from '~icons/mdi/calendar-question'
import IMdiCalendarRemove from '~icons/mdi/calendar-remove'
import IMdiCalendarStart from '~icons/mdi/calendar-start'
import IMdiCar from '~icons/mdi/car'
import IMdiFerry from '~icons/mdi/ferry'
import IMdiFlag from '~icons/mdi/flag'
import IMdiHandshakeOutline from '~icons/mdi/handshake-outline'
import IMdiLeaf from '~icons/mdi/leaf'
import IMdiLockAlertOutline from '~icons/mdi/lock-alert-outline'
import IMdiLogin from '~icons/mdi/login'
import IMdiLogout from '~icons/mdi/logout'
import IMdiMapMarkerOutline from '~icons/mdi/map-marker-outline'
import IMdiMapMarkerRadius from '~icons/mdi/map-marker-radius'
import IMdiPackageVariantClosed from '~icons/mdi/package-variant-closed'
import IMdiPierCrane from '~icons/mdi/pier-crane'
import IMdiRadioactive from '~icons/mdi/radioactive'
import IMdiSnowflake from '~icons/mdi/snowflake'
import IMdiTag from '~icons/mdi/tag'
import IMdiTruck from '~icons/mdi/truck'
import IMdiTruckDeliveryOutline from '~icons/mdi/truck-delivery-outline'
import IClarityContainerLine from '~icons/clarity/container-line'
import IClarityContainerSolid from '~icons/clarity/container-solid'
import ITablerArrowNarrowLeft from '~icons/tabler/arrow-narrow-left'
import ITablerArrowsRightLeft from '~icons/tabler/arrows-right-left'
import IIconParkOutlineRobotOne from '~icons/icon-park-outline/robot-one'

/**
 * The icons that carry domain meaning, and what each one means.
 *
 * This is the vocabulary, not a catalogue of every icon in the app. Generic UI
 * chrome — close, pencil, chevron, magnify, trash, loading — is deliberately
 * absent: nobody misreads a ✕, and pinning those down would turn the registry
 * into an unmaintained mirror of MDI. An icon belongs here when using a
 * different* one for the same concept would mislead a user, which in practice
 * means anything naming a piece of the freight domain (a terminal, a hold, a
 * cargo class) or a domain action (book, cancel, reschedule).
 *
 * `component` is the build-time icon, imported through `~icons/*` so this file
 * can render what it documents — that is what lets the Storybook page and the
 * guard test read from one source rather than a hand-kept list that drifts.
 * Components still write `<i-mdi:pier-crane>` in their own templates, which is
 * the app norm (see the Icons section in frontend/CLAUDE.md); `usedBy` names
 * the component that owns each pairing and `iconography.test.ts` fails when
 * one of them stops using its registered icon.
 *
 * The same vocabulary covers the UTAS build. UTAS renders most of its UI from
 * `src/` (see the brand-name section in frontend/CLAUDE.md), so a second
 * vocabulary would mean the shared dispatch table speaking two languages
 * depending on which bundle mounted it.
 */
export interface IconDefinition {
  /** What a user would call this thing. */
  label: string
  /** Iconify name. Must name the same icon as `component`. */
  name: string
  component: FunctionalComponent<SVGAttributes>
  /** What the icon asserts when it appears. */
  meaning: string
  /**
   * Windi class for icons that are always coloured. Omitted where the icon
   * inherits its context's colour, which is most of them — a filter prefix
   * that painted itself would fight the control it sits in.
   */
  colorClass?: string
  /**
   * The component that owns this pairing, checked by the guard test. Name the
   * shared component where there is one: a prefix icon living in
   * `TerminalMultiSelectFilter` is the reason every filter bar using it agrees.
   */
  usedBy: string
  /**
   * The concept this icon is most likely to be confused with, where that
   * confusion has actually happened or is a live risk.
   */
  notFor?: string
}

export const ICON_GROUPS = [
  'Equipment & cargo',
  'Places & parties',
  'Appointments',
  'Gate moves',
  'Status & flags',
] as const

export type IconGroup = (typeof ICON_GROUPS)[number]

export const ICONOGRAPHY: Record<
  string,
  IconDefinition & { group: IconGroup }
> = {
  containerLoaded: {
    group: 'Equipment & cargo',
    label: 'Loaded container',
    name: 'clarity:container-solid',
    component: IClarityContainerSolid,
    meaning: 'A container with cargo in it, and the Imports product area.',
    usedBy: 'components/icons/LoadedContainerIcon.vue',
    notFor:
      'Container type/size — the solid-vs-outline pair means loaded vs empty, ' +
      'not one size vs another.',
  },
  containerEmpty: {
    group: 'Equipment & cargo',
    label: 'Empty container',
    name: 'clarity:container-line',
    component: IClarityContainerLine,
    meaning:
      'A container with no cargo in it, and the Empties product area. The ' +
      'outline is the whole signal — it is the same container, minus what is ' +
      'inside it.',
    usedBy: 'components/icons/EmptyContainerIcon.vue',
    notFor:
      'Containers in general, and container type. Using it for either says ' +
      '"empty" to anyone reading the Empties nav entry two rows away.',
  },
  containerType: {
    group: 'Equipment & cargo',
    label: 'Container type',
    name: 'mdi:aspect-ratio',
    component: IMdiAspectRatio,
    meaning:
      'The size-and-type code of a container — 40HC, 20ST, 45HC. A set of ' +
      'dimensions, which is why it is not box-shaped: every box-shaped icon ' +
      'in this registry already means something else.',
    usedBy: 'components/inputs/ContainerTypeSelector.vue',
    notFor:
      'Over-dimensional cargo. Avoid the arrow-expand family here — ' +
      '`mdi:arrow-expand-all` is that flag and the two are hard to tell ' +
      'apart at 16px.',
  },
  cargoType: {
    group: 'Equipment & cargo',
    label: 'Cargo type',
    name: 'mdi:package-variant-closed',
    component: IMdiPackageVariantClosed,
    meaning:
      'What a container carries, as opposed to the container itself. Also ' +
      'the "General" option within that — general cargo is the absence of ' +
      'every special-handling flag, so the plainest box says both.',
    usedBy: 'components/exports/ExportsListFiltersBar.vue',
    notFor:
      "Container type, which is the box's size code and sits next to this " +
      'on the Exports filter bar.',
  },
  hazmat: {
    group: 'Equipment & cargo',
    label: 'Hazmat',
    name: 'mdi:radioactive',
    component: IMdiRadioactive,
    meaning: 'Hazardous cargo, which gates which slots and gates it can use.',
    colorClass: 'text-orange-500',
    usedBy: 'components/exports/EquipmentFlagIcons.vue',
  },
  reefer: {
    group: 'Equipment & cargo',
    label: 'Reefer',
    name: 'mdi:snowflake',
    component: IMdiSnowflake,
    meaning: 'Refrigerated cargo, needing a powered slot.',
    colorClass: 'text-blue-400',
    usedBy: 'components/exports/EquipmentFlagIcons.vue',
  },
  overDimensional: {
    group: 'Equipment & cargo',
    label: 'Over dimensional',
    name: 'mdi:arrow-expand-all',
    component: IMdiArrowExpandAll,
    meaning: 'Cargo exceeding standard container dimensions.',
    colorClass: 'text-purple-500',
    usedBy: 'components/exports/EquipmentFlagIcons.vue',
  },
  licensePlate: {
    group: 'Equipment & cargo',
    label: 'License plate',
    name: 'mdi:car',
    component: IMdiCar,
    meaning:
      'The truck license plate an appointment is booked against, and the ' +
      'issues arising from it being missing or in conflict.',
    usedBy: 'components/dispatch_table/IssueIcon.vue',
  },

  terminal: {
    group: 'Places & parties',
    label: 'Terminal',
    name: 'mdi:pier-crane',
    component: IMdiPierCrane,
    meaning:
      'A marine terminal — where a container is picked up, returned or ' +
      'received.',
    usedBy: 'components/inputs/TerminalMultiSelectFilter.vue',
    notFor:
      'A street-turn or yard location, which is a map marker. `mdi:domain` ' +
      'read as a terminal on the Empties list for a while; it is a generic ' +
      'office block and says nothing about a port.',
  },
  emptyLocation: {
    group: 'Places & parties',
    label: 'Location',
    name: 'mdi:map-marker-outline',
    component: IMdiMapMarkerOutline,
    meaning:
      'Where an empty physically is — a yard, a street address, a customer ' +
      'site. A place we hold rather than a terminal we transact with.',
    usedBy: 'components/emptiesList/EmptiesListView.vue',
  },
  vessel: {
    group: 'Places & parties',
    label: 'Vessel',
    name: 'mdi:ferry',
    component: IMdiFerry,
    meaning: 'The ship a container arrives on or departs on.',
    usedBy: 'components/icons/VesselIcon.vue',
  },
  shippingLine: {
    group: 'Places & parties',
    label: 'Shipping line',
    name: 'mdi:flag',
    component: IMdiFlag,
    meaning:
      'The ocean carrier (MAEU, ONEY) owning the container. A flag because ' +
      "the line is the container's allegiance, and every line already has " +
      'one in the physical world.',
    usedBy: 'components/inputs/ShippingLineSelector.vue',
  },
  customer: {
    group: 'Places & parties',
    label: 'Customer',
    name: 'mdi:handshake-outline',
    component: IMdiHandshakeOutline,
    meaning:
      'The customer a container or booking is assigned to — the party we ' +
      'are doing the work for.',
    usedBy: 'components/inputs/CustomerSelector.vue',
    notFor:
      'A user of our app. Customer and assigned-user filters sit side by ' +
      'side on the Empties bar, so the two must not share an icon.',
  },
  assignedUser: {
    group: 'Places & parties',
    label: 'Assigned user',
    name: 'mdi:account-outline',
    component: IMdiAccountOutline,
    meaning:
      'A person in the org who owns this container — a dispatcher, not a ' +
      'customer.',
    usedBy: 'components/inputs/AssignedUserMultiSelectFilter.vue',
    notFor: 'A customer, which is a handshake.',
  },
  truck: {
    group: 'Places & parties',
    label: 'Truck',
    name: 'mdi:truck',
    component: IMdiTruck,
    meaning: "A truck in the org's own fleet, and the Trucks product area.",
    usedBy: 'components/icons/TruckIcon.vue',
  },
  subCarrier: {
    group: 'Places & parties',
    label: 'Sub-carrier',
    name: 'mdi:truck-delivery-outline',
    component: IMdiTruckDeliveryOutline,
    meaning:
      "A carrier working on the org's behalf rather than the org's own " +
      'fleet.',
    usedBy: 'components/icons/SubCarrierIcon.vue',
  },
  gpsTracking: {
    group: 'Places & parties',
    label: 'GPS tracking',
    name: 'mdi:map-marker-radius',
    component: IMdiMapMarkerRadius,
    meaning:
      'Live GPS position of a truck, and the Tracking product area. The ' +
      'radius is what separates it from a plain location marker: this one ' +
      'is transmitting.',
    usedBy: 'components/icons/GPSIcon.vue',
  },

  appointment: {
    group: 'Appointments',
    label: 'Appointment',
    name: 'mdi:calendar',
    component: IMdiCalendar,
    meaning: 'A booked appointment, and the Scheduled product area.',
    usedBy: 'components/icons/AppointmentIcon.vue',
  },
  needsAppointment: {
    group: 'Appointments',
    label: 'Needs appointment',
    name: 'mdi:calendar-question',
    component: IMdiCalendarQuestion,
    meaning: 'A container that requires an appointment and has none.',
    usedBy: 'components/icons/NeedsAppointmentIcon.vue',
  },
  bookAppointment: {
    group: 'Appointments',
    label: 'Book appointment',
    name: 'mdi:calendar-plus',
    component: IMdiCalendarPlus,
    meaning: 'The action of booking a new appointment.',
    usedBy: 'components/container_row/ContainerRowActionItems.vue',
  },
  rescheduleAppointment: {
    group: 'Appointments',
    label: 'Reschedule',
    name: 'mdi:calendar-arrow-right',
    component: IMdiCalendarArrowRight,
    meaning: 'The action of moving an existing appointment to a new time.',
    usedBy: 'components/BookingSection.vue',
    notFor:
      'An ERD. The Exports list labelled its ERD with this for a while, ' +
      'which reads as "this booking was rescheduled" on a row whose whole ' +
      'point is dates.',
  },
  cancelAppointment: {
    group: 'Appointments',
    label: 'Cancel appointment',
    name: 'mdi:calendar-remove',
    component: IMdiCalendarRemove,
    meaning: 'The action of cancelling an existing appointment.',
    usedBy: 'components/CancelAppointmentButton.vue',
  },
  allAppointments: {
    group: 'Appointments',
    label: 'All appointments',
    name: 'mdi:calendar-multiselect',
    component: IMdiCalendarMultiselect,
    meaning:
      'The full appointment history of one container or booking, rather ' +
      'than its current one.',
    usedBy: 'components/ContainerDetailsTabs.vue',
  },
  gateSchedule: {
    group: 'Appointments',
    label: 'Gate schedule',
    name: 'mdi:calendar-clock',
    component: IMdiCalendarClock,
    meaning:
      'Terminal gate opening hours by shift, and the Gates product area.',
    usedBy: 'components/navigation/LeftNavBar.vue',
  },
  erd: {
    group: 'Appointments',
    label: 'ERD',
    name: 'mdi:calendar-start',
    component: IMdiCalendarStart,
    meaning:
      'Earliest receiving date — the first day a terminal will accept an ' +
      'export booking. The start of a window.',
    usedBy: 'components/exports/ExportScheduleDates.vue',
  },
  cutoffAtRisk: {
    group: 'Appointments',
    label: 'Cutoff at risk',
    name: 'mdi:calendar-alert',
    component: IMdiCalendarAlert,
    meaning:
      'A cutoff close enough to threaten the booking — the end of the ' +
      'window, with the alarm already raised.',
    usedBy: 'components/exports/ExportCutoffPill.vue',
  },
  lastFreeDay: {
    group: 'Appointments',
    label: 'Last free day',
    name: 'mdi:calendar-end',
    component: IMdiCalendarEnd,
    meaning:
      'LFD — the last day an import sits at the terminal before demurrage ' +
      'starts. The end of a window, deliberately mirroring ERD’s ' +
      'calendar-start.',
    usedBy: 'components/dispatch_table/LFDSelector.vue',
    notFor:
      'A gate schedule. The LFD filter borrowed `mdi:calendar-clock` — the ' +
      'Gates nav icon — until this entry existed.',
  },
  vesselEta: {
    group: 'Appointments',
    label: 'Vessel ETA',
    name: 'mdi:calendar-import',
    component: IMdiCalendarImport,
    meaning:
      'The date a vessel is expected to arrive, which is when its containers ' +
      'become available. An arrival into a date, not an appointment on one.',
    usedBy: 'components/filters/OtherFilters.vue',
    notFor:
      'An appointment. It used a bare `mdi:calendar` on its label, which is ' +
      'the appointment icon — invisible while it sat on a label, obvious the ' +
      'moment it moved inside the control.',
  },

  inGate: {
    group: 'Gate moves',
    label: 'In-gate',
    name: 'mdi:login',
    component: IMdiLogin,
    meaning: 'A container entering a terminal.',
    usedBy: 'components/icons/InGateIcon.vue',
  },
  outGate: {
    group: 'Gate moves',
    label: 'Out-gate',
    name: 'mdi:logout',
    component: IMdiLogout,
    meaning: 'A container leaving a terminal.',
    usedBy: 'components/icons/OutGateIcon.vue',
  },
  duals: {
    group: 'Gate moves',
    label: 'Duals',
    name: 'tabler:arrows-right-left',
    component: ITablerArrowsRightLeft,
    meaning:
      'A dual transaction — dropping an empty and collecting an import in ' +
      'one trip. Two arrows passing, because two moves happen at once.',
    usedBy: 'components/icons/DualsIcon.vue',
  },
  emptyReturn: {
    group: 'Gate moves',
    label: 'Empty return',
    name: 'tabler:arrow-narrow-left',
    component: ITablerArrowNarrowLeft,
    meaning: 'Returning an empty container to a terminal.',
    usedBy: 'components/icons/EmptyReturnIcon.vue',
  },

  holds: {
    group: 'Status & flags',
    label: 'Holds',
    name: 'mdi:lock-alert-outline',
    component: IMdiLockAlertOutline,
    meaning:
      'Customs, line or terminal holds blocking a container from being ' +
      'picked up.',
    usedBy: 'components/inputs/HoldMultiSelectFilter.vue',
  },
  autoBook: {
    group: 'Status & flags',
    label: 'Auto Book',
    name: 'icon-park-outline:robot-one',
    component: IIconParkOutlineRobotOne,
    meaning:
      "Dray Dog booking on the user's behalf. One of four icons outside " +
      'MDI, kept because MDI has no robot that reads as one at 16px.',
    usedBy: 'components/icons/RobotIcon.vue',
  },
  greenTruck: {
    group: 'Status & flags',
    label: 'Green / EV truck',
    name: 'mdi:leaf',
    component: IMdiLeaf,
    meaning:
      'A green-lane or electric truck, eligible for green appointment ' +
      'slots.',
    usedBy: 'components/icons/GreenTruckIcon.vue',
    notFor:
      'Plain "good" or "success" — green lane is a specific terminal ' +
      'programme, not a status.',
  },
  capacity: {
    group: 'Status & flags',
    label: 'Capacity',
    name: 'mdi:account-group',
    component: IMdiAccountGroup,
    meaning:
      'Fleet capacity — how many moves the org can take on, and the ' +
      'Capacity product area.',
    usedBy: 'components/icons/CapacityIcon.vue',
  },
  tag: {
    group: 'Status & flags',
    label: 'Tag',
    name: 'mdi:tag',
    component: IMdiTag,
    meaning: 'A user-defined tag applied to a container.',
    usedBy: 'components/filters/ContainerFiltersTagFilter.vue',
  },
}
